import { useSearchParams } from "react-router";
import type { DataTable, SortDir, SortState } from "@/lib/use-data-table";

/**
 * Состояние серверной таблицы консоли в адресной строке: фильтры, сортировка,
 * страница и размер страницы живут в `?…`. Ссылку на отфильтрованный список
 * можно переслать коллеге, «Назад» из карточки возвращает на ту же страницу,
 * а перезагрузка не сбрасывает срез.
 *
 * Сами строки приходят с сервера страницей — клиент больше ничего не
 * фильтрует и не сортирует (раньше списки грузились целиком, и после 500
 * товаров хвост очереди модерации становился невидимым).
 */
export type UrlTableOptions<K extends string> = {
	/** Ключи фильтров, которые хранятся в URL. */
	filters: readonly string[];
	defaultSort: SortState<K>;
	defaultPageSize?: number;
	/** Допустимые ключи сортировки — чужое значение из URL игнорируется. */
	sortKeys: readonly K[];
};

export type UrlTableState<K extends string> = {
	page: number;
	pageSize: number;
	sort: SortState<K>;
	filters: Record<string, string>;
	setFilter: (key: string, value: string) => void;
	setFilters: (patch: Record<string, string>) => void;
	resetFilters: () => void;
	hasFilters: boolean;
	setPage: (page: number) => void;
	setPageSize: (size: number) => void;
	toggleSort: (key: K) => void;
};

const PAGE_SIZES = [10, 25, 50, 100];

export function useUrlTable<K extends string>(opts: UrlTableOptions<K>): UrlTableState<K> {
	const [params, setParams] = useSearchParams();
	const defaultPageSize = opts.defaultPageSize ?? 25;

	const page = Math.max(1, Number.parseInt(params.get("page") ?? "1", 10) || 1);
	const rawSize = Number.parseInt(params.get("pageSize") ?? "", 10);
	const pageSize = PAGE_SIZES.includes(rawSize) ? rawSize : defaultPageSize;
	const rawSort = params.get("sort") as K | null;
	const rawDir = params.get("dir");
	const sort: SortState<K> =
		rawSort && opts.sortKeys.includes(rawSort)
			? { key: rawSort, dir: rawDir === "asc" ? "asc" : "desc" }
			: opts.defaultSort;

	const filters: Record<string, string> = {};
	for (const key of opts.filters) filters[key] = params.get(key) ?? "";

	const update = (mutate: (next: URLSearchParams) => void) =>
		setParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				mutate(next);
				return next;
			},
			{ replace: true },
		);

	const put = (next: URLSearchParams, key: string, value: string | undefined) => {
		if (value) next.set(key, value);
		else next.delete(key);
	};

	return {
		page,
		pageSize,
		sort,
		filters,
		// Любая смена среза возвращает на первую страницу: иначе «страница 7» от
		// прошлого фильтра открывается пустой.
		setFilter: (key, value) =>
			update((next) => {
				put(next, key, value);
				next.delete("page");
			}),
		setFilters: (patch) =>
			update((next) => {
				for (const [key, value] of Object.entries(patch)) put(next, key, value);
				next.delete("page");
			}),
		resetFilters: () =>
			update((next) => {
				for (const key of opts.filters) next.delete(key);
				next.delete("page");
			}),
		hasFilters: opts.filters.some((key) => !!filters[key]),
		setPage: (n) => update((next) => put(next, "page", n > 1 ? String(n) : undefined)),
		setPageSize: (n) =>
			update((next) => {
				put(next, "pageSize", n === defaultPageSize ? undefined : String(n));
				next.delete("page");
			}),
		toggleSort: (key) =>
			update((next) => {
				const dir: SortDir = sort.key === key ? (sort.dir === "asc" ? "desc" : "asc") : "asc";
				next.set("sort", key);
				next.set("dir", dir);
				next.delete("page");
			}),
	};
}

/**
 * Серверная страница в форме `DataTable` — чтобы переиспользовать общие
 * `SortableHead` и `DataTablePagination` без правок.
 */
export function serverDataTable<T, K extends string>(
	state: UrlTableState<K>,
	rows: T[],
	total: number,
): DataTable<T, K> {
	const pageCount = Math.max(1, Math.ceil(total / state.pageSize));
	const start = (state.page - 1) * state.pageSize;
	return {
		pageRows: rows,
		total,
		sort: state.sort,
		toggleSort: state.toggleSort,
		page: state.page,
		pageCount,
		pageSize: state.pageSize,
		setPage: state.setPage,
		setPageSize: state.setPageSize,
		rangeStart: total === 0 || rows.length === 0 ? 0 : start + 1,
		rangeEnd: total === 0 ? 0 : Math.min(start + rows.length, total),
	};
}
